import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import type { ThemeConfig } from "@/lib/theme-utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive rounded-[6px]",
  {
    variants: {
      variant: {
        default: "text-primary-foreground",
        disabled:
          "min-h-[48px] bg-[#D1D5DB] text-[#4B5563] text-[17px] w-full opacity-90 shadow-sm transition-all duration-200",
        checkoutWithCard:
          "min-h-[48px] bg-[#49BDF3] text-[#FFFFFF] text-[17px] w-full",
        processing:
          "min-h-[48px] bg-[#9CA3AF] text-[#374151] text-[17px] w-full cursor-not-allowed opacity-90 shadow-sm transition-all duration-200",
        other:
          "flex flex-col items-center gap-[6px] text-[11px] cursor-pointer",
        confirm:
          "min-h-[48px] px-[18px] bg-[#49BDF3] text-[17px] text-white font-semibold",
        backToGame:
          "min-h-[48px] w-full bg-[#49BDF3] text-[17px] text-white font-semibold",
        savedCard:
          "min-h-[48px] bg-[#000000] text-[#FFFFFF] text-[17px] w-full flex items-center justify-center gap-3",
        back: "mb-1",
      },

      size: {
        default: "cursor-pointer",
        sm: "h-8 gap-1.5 mx-4 cursor-pointer",
        lg: "h-10 mx-4 cursor-pointer",
        box: "cursor-pointer",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

/**
 * Get theme-based button styles for variants that use the primary color
 * @param variant - The button variant
 * @param theme - The theme configuration
 * @returns CSS properties object for themed styling
 */
function getThemedButtonStyle(
  variant: string | null | undefined,
  theme?: ThemeConfig
): React.CSSProperties {
  if (!theme || theme.name === "superplay") {
    return {};
  }

  // Variants that should use theme primary color
  const primaryVariants = ["checkoutWithCard", "confirm", "backToGame"];
  
  if (variant && primaryVariants.includes(variant)) {
    return {
      backgroundColor: theme.colors.primary,
      borderRadius: theme.buttonRadius,
    };
  }

  // For disabled variant, only apply border radius
  if (variant === "disabled") {
    return {
      borderRadius: theme.buttonRadius,
    };
  }

  return {};
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  theme,
  style,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    theme?: ThemeConfig;
  }) {
  const Comp = asChild ? Slot : "button";
  const themedStyle = getThemedButtonStyle(variant, theme);

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      style={{ ...themedStyle, ...style }}
      {...props}
    />
  );
}

export { Button, buttonVariants, getThemedButtonStyle };
